
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Music, BarChart2, Users, Calendar, Library, DollarSign } from 'lucide-react';

interface FeatureProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  delay: string;
}

const Feature: React.FC<FeatureProps> = ({ title, description, icon, delay }) => (
  <Card 
    variant="neo" 
    className="animate-fade-in hover-lift"
    style={{ animationDelay: delay }}
  >
    <CardContent className="p-4 flex flex-col items-center text-center">
      <div className="mb-3 p-2 rounded-xl bg-black/30 backdrop-blur-sm">
        {icon}
      </div>
      <h3 className="text-lg font-bold mb-1 text-white">{title}</h3>
      <p className="text-white/70 text-sm">{description}</p>
    </CardContent>
  </Card>
);

const FeaturesSection: React.FC = () => {
  const features = [
    {
      title: "Music Distribution",
      description: "Upload, organize, and distribute your music to all major streaming platforms.",
      icon: <Music className="h-10 w-10 text-neon-purple" />,
      delay: "0.1s"
    },
    {
      title: "Analytics Dashboard",
      description: "Track your streaming performance, audience growth, and campaign results.",
      icon: <BarChart2 className="h-10 w-10 text-neon-blue" />,
      delay: "0.2s"
    },
    {
      title: "Musicians Network",
      description: "Connect with other artists for collaborations and networking opportunities.",
      icon: <Users className="h-10 w-10 text-neon-pink" />,
      delay: "0.3s"
    },
    {
      title: "Royalty Management",
      description: "Track and manage your royalties across different platforms and territories.",
      icon: <DollarSign className="h-10 w-10 text-neon-green" />,
      delay: "0.4s"
    },
    {
      title: "Gig Management",
      description: "Organize your tour dates, bookings, and live performance schedules.",
      icon: <Calendar className="h-10 w-10 text-neon-yellow" />,
      delay: "0.5s"
    },
    {
      title: "Music Library",
      description: "Organize your songs, stems, and samples in one centralized location.",
      icon: <Library className="h-10 w-10 text-neon-purple" />,
      delay: "0.6s"
    }
  ];
  
  return (
    <section className="py-8 px-4 sm:px-6 md:px-8 lg:px-16 max-w-7xl mx-auto">
      <h2 className="text-3xl font-bold text-center text-white mb-8 animate-fade-in">Key Features</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {features.map((feature, index) => (
          <Feature 
            key={index} 
            title={feature.title} 
            description={feature.description} 
            icon={feature.icon} 
            delay={feature.delay} 
          />
        ))}
      </div>
    </section>
  );
};

export default FeaturesSection;
